// Full-screen annotator overlay launched when the operator clicks a Roof
// Diagram slot's thumbnail/icon. Each slot keeps its own pin set (filtered
// via the `slot` prop on RoofDiagramAnnotator). When the slot's template is
// a Google Earth aerial, an opacity slider lets the operator fade the
// underlying image so freshly-drawn measurement lines are easier to see.
//
// Props
//   open         — boolean, controls visibility
//   slotKey      — one of 'roof_plan' / 'roofer' / 'guttering' / 'concrete_restore' / 'painter'
//   slotLabel    — display label (e.g. "Roof Plan")
//   slotColour   — accent colour for the modal header strip
//   appraisalId  — the appraisal id (passed to the annotator for autosave)
//   templateId   — the template image to load as the canvas
//   pins         — the FULL pin array from the appraisal (annotator filters
//                  to its own slot, merges back on save)
//   onPinsChange — called with the FULL merged pin array
//   onClose      — close-handler
//   onChangeTemplate — optional callback to swap the template (e.g.
//                  "Pick a different diagram") — surfaced as a button in
//                  the toolbar.

import React, { useState, useEffect, useRef } from 'react';
import { X, Image as ImageIcon, Copy, RefreshCw } from 'lucide-react';
import RoofDiagramAnnotator from '@/components/RoofDiagramAnnotator';

const SLOT_KEY_TO_SHORT = {
  roof_plan_template_id: 'roof_plan',
  roofer_measurements_template_id: 'roofer',
  guttering_measurements_template_id: 'guttering',
  concrete_restore_measurements_template_id: 'concrete_restore',
  painter_measurements_template_id: 'painter',
};

// Trade → relevant pin types. Used by the "Pull from Roof Plan" action to
// copy ONLY pins that belong to that trade from the master canvas. Pin
// types not listed here are dropped on copy.
//   • Roofing          — cladding system + Spouting (perimeter context — a
//                        roofer needs to see the outline of the roof when
//                        measuring sheets / tiles / flashings) + Pipe/Flue
//                        penetrations (they affect tile cuts + flashings)
//   • Guttering        — water-shedding components
//   • Concrete Restore — concrete-tile roof restoration needs the FULL outline
//                        (Ridge / Valley / Hip / Barge) plus Face areas to
//                        repaint, edge Flashings, and the Pipe/Flue
//                        penetrations that need to be coated around.
//   • Painter          — full exterior repaint scope: roof Face areas to
//                        spray, all linear trim (Ridge / Hip / Barge /
//                        Valley / Flashing), gutters (Spouting) which the
//                        painter typically coats, plus Pipe/Flue
//                        penetrations to cut in around.
// HOTFIX 2026-09-14 (Audit D P2): pin-type vocabulary drift. `Pipe`, `Flue`,
// `Downpipe` were merged/renamed into `Penetration` and `Outlet` respectively.
// Ticking the old names did nothing; `Penetration` wasn't rendered.
const SLOT_PIN_TYPE_FILTER = {
  roofer:           ['Ridge', 'Valley', 'Hip', 'Barge', 'Flashing', 'Face', 'Length', 'Spouting', 'Penetration'],
  guttering:        ['Spouting', 'Outlet'],
  concrete_restore: ['Ridge', 'Valley', 'Hip', 'Barge', 'Flashing', 'Face', 'Length', 'Penetration'],
  painter:          ['Ridge', 'Valley', 'Hip', 'Barge', 'Flashing', 'Face', 'Length', 'Spouting', 'Penetration'],
};
// roof_plan = master (no filter — operator drops pins there)

// Master pin-type catalogue — every pin type the system knows about.
// Used by the per-slot "Pin types" checkbox key inside the slot modal so the
// operator can include/exclude individual types when pulling from the
// Master Roof Plan. The defaults come from SLOT_PIN_TYPE_FILTER; this list
// just controls which checkboxes are rendered.
const ALL_PIN_TYPES = ['Ridge', 'Valley', 'Hip', 'Barge', 'Flashing', 'Face', 'Length', 'Spouting', 'Outlet', 'Penetration'];

export default function SlotAnnotatorModal({
  open,
  slotKey,
  slotLabel,
  slotColour = '#D35400',
  appraisalId,
  templateId,
  roofPlanTemplateId,
  pins,
  onPinsChange,
  onTemplateAligned,
  onClose,
  onChangeTemplate,
  api,
  roofProfile,
  roofType,
}) {
  // Background opacity — defaults to 100% but the operator can dim a busy
  // Google Earth screenshot so freshly-drawn measurement lines pop.
  const [pulling, setPulling]     = useState(false);
  const [pullFlash, setPullFlash] = useState(null);
  // Operator-overridable pin-type filter — seeded from the slot's default
  // recipe (SLOT_PIN_TYPE_FILTER). Operator can untick types they don't
  // want pulled from the Master Roof Plan, or tick extras (e.g. include
  // Spouting on a Painter slot for a job that includes gutter painting).
  const [activeTypes, setActiveTypes] = useState(() => new Set());
  const [filterOpen, setFilterOpen]   = useState(false);
  // Tracks which types are currently mirrored from Master into this slot.
  // Drives the LIVE filter behaviour — when the operator ticks Hip in the
  // checkbox grid, we diff against this ref and pull/remove the right
  // pins from this slot so the canvas updates immediately.
  const appliedTypesRef = useRef(null);   // null = not yet initialised
  // Optimistic pin snapshot used by rapid back-to-back toggles. Each
  // applyTypeDiff computes the merged pin array from this ref so a
  // burst of clicks (Hip, then Barge, then Flashing) doesn't race
  // against the parent's async re-render.
  const localPinsRef = useRef(null);

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Reset the type filter to the slot's recipe whenever the slot changes
  // (or the modal re-opens) so the operator starts from the sensible
  // default but can still tweak before pulling.
  const shortSlot   = SLOT_KEY_TO_SHORT[slotKey] || slotKey;
  const defaultTypes = SLOT_PIN_TYPE_FILTER[shortSlot] || ALL_PIN_TYPES;
  useEffect(() => {
    if (!open) return;
    // Initialise the checkbox grid from the types ALREADY present in
    // this slot — so the boxes accurately reflect what the operator
    // sees on the canvas. Falls back to the slot's recipe when the
    // slot is empty (typical first-open).
    const currentInSlot = new Set(
      (pins || [])
        .filter(p => (p.slot || 'roof_plan') === shortSlot)
        .map(p => p.type)
    );
    const seed = currentInSlot.size > 0 ? currentInSlot : new Set(defaultTypes);
    setActiveTypes(seed);
    setFilterOpen(false);
    appliedTypesRef.current = currentInSlot;
    localPinsRef.current = pins || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, slotKey]);

  // Whenever the parent flushes a new pins array down, refresh the
  // optimistic snapshot. Without this, every parent-driven update would
  // be drowned out by the stale localPinsRef.
  useEffect(() => {
    if (!open) return;
    localPinsRef.current = pins || [];
  }, [pins, open]);

  // Iter182++ — Defensive templateId latch. The parent derives templateId
  // from `draft[slotKey] || saved[slotKey] || ''`. After a pin-drag save,
  // the parent reloads the appraisal; if the reload response is briefly
  // missing roof_plan_template_id the template would vanish, and the
  // editor would flash to "No diagram is linked to this slot yet".
  // Cache the last NON-EMPTY templateId for this open session so a
  // transient blank doesn't kill the canvas.
  const stickyTemplateIdRef = useRef('');
  useEffect(() => {
    if (open && templateId) stickyTemplateIdRef.current = templateId;
    if (!open) stickyTemplateIdRef.current = '';
  }, [open, templateId]);
  const effectiveTemplateId = templateId || stickyTemplateIdRef.current;

  if (!open) return null;

  const allowedTypes = Array.from(activeTypes);
  // Untouched-from-default state — used to show a "(default)" hint next
  // to the filter strip.
  const filterUntouched = (
    allowedTypes.length === defaultTypes.length
    && allowedTypes.every(t => defaultTypes.includes(t))
  );
  // Slot-specific cosmetic label overrides. Roofing trade doesn't do
  // gutters — so render the Spouting pin type as "Perimeter" in black on
  // that slot. The underlying pin data still uses type:"Spouting" so
  // cross-slot pulls + the Master view stay consistent.
  const labelOverrides = shortSlot === 'roofer' ? { Spouting: 'Perimeter' } : {};

  // Generate a fresh pin id (mirrors the helper inside the annotator).
  const genId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

  // ────────────────────────────────────────────────────────────────────
  // LIVE FILTER SYNC
  // When the operator ticks a pin type in the "Pin types to pull" filter,
  // that type's pins should appear on this slot immediately (and vice
  // versa for un-ticking). Diff the just-toggled type against the last
  // applied baseline and add/remove the corresponding pins from this
  // slot, persisting silently. The "Pull from Master Roof Plan" button
  // is still available for an explicit full-slot replace with confirm.
  const applyTypeDiff = async (typeKey, added) => {
    if (!api || !appraisalId) return;
    const all = localPinsRef.current || pins || [];
    let merged;
    if (added) {
      // Add: copy any Master pins of typeKey that aren't already on this slot
      const masterMatches = all.filter(p =>
        (p.slot || 'roof_plan') === 'roof_plan' && p.type === typeKey
      );
      if (masterMatches.length === 0) {
        setPullFlash({ ok: false, msg: `No ${typeKey} pins on Master Roof Plan to mirror yet — drop them on the Master first.` });
        setTimeout(() => setPullFlash(null), 4000);
        return;
      }
      const copied = masterMatches.map(p => ({ ...p, id: genId(), slot: shortSlot }));
      merged = [...all, ...copied];
      localPinsRef.current = merged;
      try {
        await api.patch(`/appraisals/${appraisalId}/roof-diagram-annotations`, { annotations: merged });
        onPinsChange?.(merged);
        setPullFlash({ ok: true, msg: `Mirrored ${copied.length} ${typeKey} pin${copied.length === 1 ? '' : 's'} from Master Roof Plan.` });
        setTimeout(() => setPullFlash(null), 3500);
      } catch (e) {
        setPullFlash({ ok: false, msg: e?.response?.data?.detail || `Couldn't mirror ${typeKey}` });
        setTimeout(() => setPullFlash(null), 4000);
      }
    } else {
      // Remove: drop all pins of typeKey from this slot only
      const removedCount = all.filter(p =>
        (p.slot || 'roof_plan') === shortSlot && p.type === typeKey
      ).length;
      if (removedCount === 0) return;
      merged = all.filter(p =>
        !((p.slot || 'roof_plan') === shortSlot && p.type === typeKey)
      );
      localPinsRef.current = merged;
      try {
        await api.patch(`/appraisals/${appraisalId}/roof-diagram-annotations`, { annotations: merged });
        onPinsChange?.(merged);
        setPullFlash({ ok: true, msg: `Removed ${removedCount} ${typeKey} pin${removedCount === 1 ? '' : 's'} from ${slotLabel}.` });
        setTimeout(() => setPullFlash(null), 3500);
      } catch (e) {
        setPullFlash({ ok: false, msg: e?.response?.data?.detail || `Couldn't remove ${typeKey}` });
        setTimeout(() => setPullFlash(null), 4000);
      }
    }
  };

  // Wrapper around setActiveTypes that ALSO triggers the live diff for
  // the type that was just toggled. Used by every checkbox / bulk button
  // so a single code path drives both the filter UI and the canvas sync.
  const toggleType = (typeKey, on) => {
    setActiveTypes(prev => {
      const next = new Set(prev);
      if (on) next.add(typeKey); else next.delete(typeKey);
      return next;
    });
    // Update the applied baseline optimistically so a rapid toggle
    // re-tick doesn't spam duplicates from Master.
    if (appliedTypesRef.current) {
      const applied = appliedTypesRef.current;
      const was = applied.has(typeKey);
      if (on && !was) { applied.add(typeKey); applyTypeDiff(typeKey, true); }
      else if (!on && was) { applied.delete(typeKey); applyTypeDiff(typeKey, false); }
    }
  };
  const bulkSetTypes = (nextSet) => {
    // Determine the diff against the applied baseline so we only fire
    // network ops for actual changes (handles Pull all / Remove all).
    const applied = appliedTypesRef.current || new Set();
    const added   = [...nextSet].filter(t => !applied.has(t));
    const removed = [...applied].filter(t => !nextSet.has(t));
    setActiveTypes(new Set(nextSet));
    appliedTypesRef.current = new Set(nextSet);
    added.forEach(t => applyTypeDiff(t, true));
    removed.forEach(t => applyTypeDiff(t, false));
  };
  // ────────────────────────────────────────────────────────────────────

  // Copy matching-type pins from the master Roof Plan slot into THIS slot.
  // Each copy gets a fresh id so editing the copy never touches the master.
  // Existing pins on THIS slot are replaced (after confirmation). If the
  // slot's CANVAS template differs from the master's, the canvas is also
  // re-pointed at the master's template — pins are calibrated to the
  // master's image, so copying without aligning the canvas would land the
  // markers in the wrong places.
  const pullFromRoofPlan = async () => {
    if (!api || !appraisalId) return;
    if (allowedTypes.length === 0) {
      setPullFlash({ ok: false, msg: 'Tick at least one pin type to pull.' });
      setTimeout(() => setPullFlash(null), 3500);
      return;
    }
    const all = pins || [];
    const sourcePins = all.filter(p => (p.slot || 'roof_plan') === 'roof_plan');
    const matching = sourcePins.filter(p => allowedTypes.includes(p.type));
    if (matching.length === 0) {
      setPullFlash({ ok: false, msg: `No matching pins on Roof Plan to copy (${allowedTypes.join(' / ')}).` });
      setTimeout(() => setPullFlash(null), 4500);
      return;
    }
    const existingMine = all.filter(p => (p.slot || 'roof_plan') === shortSlot).length;
    const needsTemplateAlign = !!(roofPlanTemplateId && roofPlanTemplateId !== templateId);
    const proceed = window.confirm(
      `Mirror ${slotLabel} from Master Roof Plan?\n\n` +
      `• Copy ${matching.length} pin${matching.length === 1 ? '' : 's'} (${allowedTypes.join(', ')})` +
      (existingMine > 0 ? `\n• Replace the ${existingMine} pin${existingMine === 1 ? '' : 's'} currently on ${slotLabel}` : '') +
      (needsTemplateAlign ? `\n• Align the canvas to Master Roof Plan's template (so pins land in the right places)` : '') +
      `\n\nContinue?`
    );
    if (!proceed) return;
    setPulling(true);
    try {
      const copied = matching.map(p => ({ ...p, id: genId(), slot: shortSlot }));
      const otherSlots = all.filter(p => (p.slot || 'roof_plan') !== shortSlot);
      const merged = [...otherSlots, ...copied];
      await api.patch(`/appraisals/${appraisalId}/roof-diagram-annotations`, { annotations: merged });
      // Re-point the canvas at the master's template if it currently differs.
      if (needsTemplateAlign) {
        await api.patch(`/appraisals/${appraisalId}/roof-template`, { [slotKey]: roofPlanTemplateId });
        onTemplateAligned?.(slotKey, roofPlanTemplateId);
      }
      onPinsChange?.(merged);
      const msg = needsTemplateAlign
        ? `Copied ${matching.length} pin${matching.length === 1 ? '' : 's'} and aligned the canvas to the Master Roof Plan template.`
        : `Copied ${matching.length} pin${matching.length === 1 ? '' : 's'} from Master Roof Plan.`;
      setPullFlash({ ok: true, msg });
      setTimeout(() => setPullFlash(null), 5000);
    } catch (e) {
      setPullFlash({ ok: false, msg: e?.response?.data?.detail || 'Copy failed' });
      setTimeout(() => setPullFlash(null), 5000);
    } finally {
      setPulling(false);
    }
  };

  return (
    <div
      data-testid="slot-annotator-modal"
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
    >
      <div className="bg-white rounded-md shadow-xl w-full max-w-7xl flex flex-col" style={{ maxHeight: '96vh' }}>
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200"
          style={{ borderTop: `4px solid ${slotColour}` }}
        >
          <div className="flex items-center gap-3">
            <ImageIcon size={16} style={{ color: slotColour }} />
            <div>
              <h3 className="text-sm font-bold text-slate-800">{slotLabel} — Diagram editor</h3>
              <p className="text-[11px] text-slate-500">
                Pins on this slot are kept independent from the other slots.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {shortSlot !== 'roof_plan' && (
              <button
                data-testid="slot-annot-pull-from-master"
                onClick={pullFromRoofPlan}
                disabled={pulling}
                title={`Copy ${allowedTypes.length} pin type${allowedTypes.length === 1 ? '' : 's'} (${allowedTypes.join(' / ') || 'none selected'}) from Master Roof Plan into ${slotLabel}. Each copy is fully editable.`}
                className="flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-[#0F4F1B] text-[#0F4F1B] hover:bg-[#0F4F1B] hover:text-white transition-all disabled:opacity-50"
              ><Copy size={11} /> {pulling ? 'Pulling…' : 'Pull from Master Roof Plan'}</button>
            )}
            {/* Iter193 — Sync now. Force-resync this slot from the
                Master via the backend's bulk sync endpoint. Wipes any
                stale mirrors and re-mirrors the current master,
                respecting the operator's per-slot pin-type filter.
                Operator-drawn extras (non-mirror pins) on this slot
                survive the sync. */}
            {shortSlot !== 'roof_plan' && (
              <button
                data-testid="slot-annot-sync-now"
                onClick={async () => {
                  setPulling(true);
                  try {
                    const { data } = await api.post(`/appraisals/${appraisalId}/slots/${shortSlot}/sync`);
                    const next = Array.isArray(data?.roof_diagram_annotations) ? data.roof_diagram_annotations : [];
                    localPinsRef.current = next;
                    onPinsChange?.(next);
                    setPullFlash({ ok: true, msg: `${slotLabel} synced with Master Roof Plan.` });
                    setTimeout(() => setPullFlash(null), 3500);
                  } catch (e) {
                    setPullFlash({ ok: false, msg: e?.response?.data?.detail || 'Sync failed' });
                    setTimeout(() => setPullFlash(null), 4000);
                  } finally {
                    setPulling(false);
                  }
                }}
                disabled={pulling}
                title="Force-resync this slot with the Master Roof Plan. Operator-drawn extras on this slot are kept."
                className="flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-sky-600 text-sky-700 hover:bg-sky-600 hover:text-white transition-all disabled:opacity-50"
              ><RefreshCw size={11} /> Sync now</button>
            )}
            {onChangeTemplate && (
              <button
                data-testid="slot-annot-change-template"
                onClick={onChangeTemplate}
                className="text-xs px-2.5 py-1 border border-slate-200 rounded text-slate-600 hover:bg-slate-100"
              >Change template…</button>
            )}
            <button
              data-testid="slot-annot-close"
              onClick={onClose}
              className="p-1.5 rounded hover:bg-slate-100"
              title="Close (Esc)"
            ><X size={16} /></button>
          </div>
        </div>

        {/* Body — opacity wraps the annotator so the slider only fades the
            background image rendered inside it (pins remain at 100%). */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50">
          {pullFlash && (
            <div
              data-testid="slot-annot-pull-flash"
              className={`mb-3 px-3 py-2 rounded text-xs font-semibold ${pullFlash.ok ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'}`}
            >{pullFlash.msg}</div>
          )}
          {shortSlot !== 'roof_plan' && (
            <div className="mb-3 bg-white border border-slate-200 rounded-md p-2.5 space-y-2" data-testid="slot-annot-pin-filter">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setFilterOpen(v => !v)}
                  data-testid="slot-annot-pin-filter-toggle"
                  className="text-[11px] uppercase tracking-[0.12em] font-bold text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"
                >
                  Pin types to pull
                  <span className="text-slate-400">{filterOpen ? '▾' : '▸'}</span>
                  {!filterUntouched && (
                    <span className="ml-1 text-[9px] text-amber-700 bg-amber-50 ring-1 ring-amber-200 px-1 py-0.5 rounded">customised</span>
                  )}
                </button>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Bulk Pull all / Remove all — quickly toggle every pin
                      type without ticking eleven boxes individually. */}
                  <button
                    type="button"
                    data-testid="slot-annot-pin-filter-all"
                    onClick={() => bulkSetTypes(new Set(ALL_PIN_TYPES))}
                    className="text-[10px] font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 ring-1 ring-emerald-200 rounded px-1.5 py-0.5"
                  >Pull all</button>
                  <button
                    type="button"
                    data-testid="slot-annot-pin-filter-none"
                    onClick={() => bulkSetTypes(new Set())}
                    className="text-[10px] font-bold text-rose-700 bg-rose-50 hover:bg-rose-100 ring-1 ring-rose-200 rounded px-1.5 py-0.5"
                  >Remove all</button>
                  {allowedTypes.length === 0 ? (
                    <span className="text-[10px] text-rose-600 italic">No types selected — slot is cleared of Master-mirrored pins.</span>
                  ) : (
                    <span className="text-[10px] text-slate-500">
                      <span className="font-bold text-emerald-700">LIVE</span> · {allowedTypes.length} type{allowedTypes.length === 1 ? '' : 's'} ·{' '}
                      <span className="font-mono text-slate-700">{allowedTypes.map(t => labelOverrides[t] || t).join(', ')}</span>
                    </span>
                  )}
                  {!filterUntouched && (
                    <button
                      type="button"
                      data-testid="slot-annot-pin-filter-reset"
                      onClick={() => bulkSetTypes(new Set(defaultTypes))}
                      className="text-[10px] text-slate-500 hover:text-slate-800 underline"
                    >Reset to {slotLabel} defaults</button>
                  )}
                </div>
              </div>
              {filterOpen && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5 pt-1 border-t border-slate-100">
                  {ALL_PIN_TYPES.map(t => {
                    const on = activeTypes.has(t);
                    const isDefault = defaultTypes.includes(t);
                    const displayLabel = labelOverrides[t] || t;
                    return (
                      <label
                        key={t}
                        data-testid={`slot-annot-pin-type-${t.toLowerCase()}`}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded text-[11px] cursor-pointer transition-colors ${on ? 'bg-emerald-50 ring-1 ring-emerald-200 text-emerald-900' : 'bg-slate-50 ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100'}`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={(e) => toggleType(t, e.target.checked)}
                          className="accent-emerald-600"
                        />
                        <span className="font-mono">{displayLabel}</span>
                        {isDefault && <span className="text-[8px] text-slate-400 ml-auto">def</span>}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {!effectiveTemplateId ? (
            <div data-testid="slot-annot-empty" className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-800">
              No diagram is linked to this slot yet. Click <strong>Change template…</strong> above to pick one, or upload a Google Earth screenshot via the slots card.
            </div>
          ) : (
            <div className="slot-annot-canvas">
              <RoofDiagramAnnotator
                appraisalId={appraisalId}
                templateId={effectiveTemplateId}
                initialPins={pins}
                onChange={onPinsChange}
                slot={shortSlot}
                roofProfile={roofProfile}
                roofType={roofType}
                pinTypeLabelOverrides={labelOverrides}
                forceRidgeCapCount={shortSlot === 'concrete_restore'}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
